import { Alert, Box, Snackbar, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ElectronicObjectRow from './ElectronicObjectRow';
import ElectronicsModal from './ElectronicsModal';
import MetalObjectRow from './MetalObjectRow';
import ObjectCategory from './ObjectCategory';
import {
    ELECTRONICS_TYPE,
    OTHERS_TYPE,
    STONE_PERCENT_OPTIONS,
    groupDeskMetalObjects,
    isSilverMetal,
} from '../domain/contracts/pawnObjectDesk';
import { scaleErrorKey, type ScaleErrorCode } from '../domain/scaleReader';
import type { PawnObjectDesk as PawnObjectDeskState } from '../hooks/usePawnObjectDesk';
import { useScaleReader } from '../hooks/useScaleReader';

const electronicSections = [
    { type: ELECTRONICS_TYPE, titleKey: 'contracts.categories.electronics' },
    { type: OTHERS_TYPE, titleKey: 'contracts.categories.others' },
];

interface PawnObjectDeskProps {
    desk: PawnObjectDeskState;
    /**
     * Session verification: unit prices and values stay exactly as the desk priced them,
     * because the server recalculates them from the same price configuration.
     */
    lockPrices?: boolean;
}

/**
 * The pawning object desk: the metal and electronics/other rows an operator fills in.
 * Rendered by contract creation and, as in Delphi's `VerificareAmanet` mode, by
 * previous-session verification.
 */
const PawnObjectDesk = ({ desk, lockPrices = false }: PawnObjectDeskProps) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const scale = useScaleReader();
    const [scaleRow, setScaleRow] = useState<number | null>(null);
    const [scaleError, setScaleError] = useState<ScaleErrorCode | null>(null);

    // One weight into one row: on success through the same change handler as typing, so the
    // stone weight, the proposed value and the debounced server price follow exactly as they do
    // for a typed value. On failure the field is left as it was.
    const readScaleInto = async (index: number) => {
        setScaleRow(index);
        setScaleError(null);

        try {
            const result = await scale.read();

            if (result?.ok) {
                desk.handleMetalObjectChange(index, 'cantitate', result.reading.grams);
            } else if (result) {
                setScaleError(result.code);
            }
        } finally {
            setScaleRow(null);
        }
    };

    return (
        <>
            <Tabs value={activeTab} onChange={(_event, value: number) => setActiveTab(value)}>
                <Tab label={t('contracts.metalObjects')} />
                <Tab label={t('contracts.electronicObjects')} />
            </Tabs>

            {activeTab === 0 && (
                <Box mt={3}>
                    <Stack spacing={2}>
                        <Typography variant="h6">{t('contracts.metalObjects')}</Typography>
                        {groupDeskMetalObjects(desk.metalObjects).map(group => (
                            <ObjectCategory
                                key={group.key}
                                title={group.title ?? t('contracts.categories.otherKarats')}
                            >
                                {group.rows.map(({ obj, index }) => (
                                    <MetalObjectRow
                                        key={obj.id}
                                        obj={obj}
                                        originalIndex={index}
                                        stonePercentOptions={STONE_PERCENT_OPTIONS}
                                        onObjectChange={desk.handleMetalObjectChange}
                                        showStoneFields={
                                            group.title === null || !isSilverMetal(obj.tipMetal)
                                        }
                                        isCustomMetalRow={group.title === null}
                                        metalTypeOptions={desk.metalTypeOptions}
                                        karatOptions={desk.getCustomKaratOptions(obj.tipMetal)}
                                        onCustomMetalSelectionChange={
                                            desk.handleCustomMetalSelection
                                        }
                                        lockPrices={lockPrices}
                                        onReadScale={
                                            scale.available
                                                ? () => void readScaleInto(index)
                                                : undefined
                                        }
                                        scaleReading={scaleRow === index}
                                    />
                                ))}
                            </ObjectCategory>
                        ))}
                    </Stack>
                </Box>
            )}

            {activeTab === 1 && (
                <Box mt={3}>
                    <Stack spacing={2}>
                        <Typography variant="h6">{t('contracts.electronicObjects')}</Typography>
                        {electronicSections.map(section => (
                            <ObjectCategory key={section.type} title={t(section.titleKey)}>
                                {desk.electronicObjects.map((obj, index) =>
                                    obj.tip === section.type ? (
                                        <ElectronicObjectRow
                                            key={obj.id}
                                            obj={obj}
                                            originalIndex={index}
                                            onObjectChange={desk.handleElectronicObjectChange}
                                            onOpenElectronicsModal={
                                                section.type === ELECTRONICS_TYPE
                                                    ? desk.openElectronicsModal
                                                    : undefined
                                            }
                                            lockAmounts={lockPrices}
                                        />
                                    ) : null,
                                )}
                            </ObjectCategory>
                        ))}
                    </Stack>
                </Box>
            )}

            <Snackbar
                open={scaleError !== null}
                autoHideDuration={8000}
                onClose={() => setScaleError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="error"
                    onClose={() => setScaleError(null)}
                    data-testid="scale-error"
                >
                    {scaleError === null ? '' : t(scaleErrorKey(scaleError))}
                </Alert>
            </Snackbar>

            <ElectronicsModal
                open={desk.electronicsModalOpen}
                onClose={desk.closeElectronicsModal}
                onSave={desk.saveElectronicsModal}
            />
        </>
    );
};

export default PawnObjectDesk;
